import { useState, useEffect } from 'react'
import { formatCurrency } from '../../utils/formatters'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material'
import { Receipt as ReceiptIcon } from '@mui/icons-material'
import { useStiRates, useUpdateStiRates } from '../../hooks/api/useStiRates'
import { StiRateEntry } from '../../types'

interface StiRatesModalProps {
  open: boolean
  onClose: () => void
  customerId: string
  customerName: string
}

const StiRatesModal = ({ open, onClose, customerId, customerName }: StiRatesModalProps) => {
  const [rates, setRates] = useState<StiRateEntry[]>([])

  const { data, isLoading, error } = useStiRates(customerId, open)
  const updateMutation = useUpdateStiRates()

  // Sync local state when data loads
  useEffect(() => {
    if (data?.rates) {
      setRates(data.rates.map((r) => ({ ...r })))
    }
  }, [data])

  const handleRateChange = (skuId: string, value: string) => {
    setRates((prev) =>
      prev.map((r) =>
        r.sku_id === skuId
          ? { ...r, sti_rate: value === '' ? null : Number(value) }
          : r
      )
    )
  }

  const handleSave = () => {
    // Only send rows that have a rate configured
    const payload = rates
      .filter((r) => r.sti_rate !== null && r.sti_rate !== undefined)
      .map((r) => ({ sku_id: r.sku_id as string, rate: r.sti_rate as number }))

    updateMutation.mutate(
      { customerId, rates: payload },
      { onSuccess: onClose }
    )
  }

  const hasUnsavedChanges = () => {
    if (!data?.rates) return false
    return rates.some((r, i) => r.sti_rate !== data.rates[i]?.sti_rate)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ReceiptIcon color="warning" />
          <Box>
            <Typography variant="h6">Manage STI Rates</Typography>
            <Typography variant="body2" color="text.secondary">
              {customerName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Set per-SKU rates for this Sales Tax Invoice customer. Leave a field blank to
          use the standard rate. These rates are applied to all future STI bills for this
          customer.
        </Alert>

        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">Failed to load STI rates. Please try again.</Alert>
        )}

        {!isLoading && !error && rates.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>SKU Name</strong></TableCell>
                  <TableCell align="center"><strong>Packing</strong></TableCell>
                  <TableCell align="right"><strong>Standard Rate</strong></TableCell>
                  <TableCell align="right" sx={{ minWidth: 140 }}>
                    <strong>STI Rate (Rs)</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rates.map((row) => (
                  <TableRow
                    key={row.sku_id as string}
                    sx={{
                      '&:last-child td': { border: 0 },
                      backgroundColor: row.sti_rate !== null ? 'action.hover' : undefined,
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {row.sku_name}
                        {row.sti_rate !== null && (
                          <Chip label="set" size="small" color="success" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">{row.packing}</TableCell>
                    <TableCell align="right">Rs {formatCurrency(row.standard_rate)}</TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={row.sti_rate ?? ''}
                        onChange={(e) => handleRateChange(row.sku_id as string, e.target.value)}
                        placeholder="e.g. 860"
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && !error && rates.length === 0 && (
          <Typography color="text.secondary" textAlign="center" py={3}>
            No SKUs found for this company.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={updateMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSave}
          disabled={isLoading || updateMutation.isPending || !hasUnsavedChanges()}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Rates'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default StiRatesModal
